import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Redo2, Shirt, Star, Type, Undo2, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useCanvasStateManager,
  useCanUndo,
  useCanRedo,
} from '@/stores/canvasStateManager';

/** Tools of the rail that open a drawer. */
export type DirectSaleDesktopPanel = 'upload' | 'motifs' | 'font' | 'color';
export type DirectSaleRailAction = 'products' | 'upload' | 'text' | 'motifs';

// min-h instead of h: a label that needs two lines in a longer language
// grows the tile instead of being cut off.
const TILE =
  'flex min-h-[62px] w-[88px] flex-col items-center justify-center gap-1 rounded-control px-1 py-1.5 text-center text-[12px] font-semibold leading-tight transition-colors [overflow-wrap:anywhere] hover:bg-paper-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary';

const HISTORY =
  'flex h-10 w-[88px] items-center justify-center rounded-control transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed';

/**
 * The vertical tool rail on the left edge of the desktop editor. It is
 * controlled: the design step owns which drawer is open, because that state
 * also reacts to the canvas selection and to Escape.
 *
 * Only "Uploads" and "Motive" are toggles. "Produkte" opens a dialog and
 * "Text" puts a text object straight onto the product, so neither has a
 * drawer that could be "open" — they stay plain buttons without
 * `aria-pressed`, otherwise a screen reader would announce a state that
 * never changes.
 */
export function DirectSaleDesktopRail({
  activePanel,
  onAction,
}: {
  /** Which drawer is open; marks 'upload' / 'motifs' as pressed. */
  activePanel: DirectSaleDesktopPanel | null;
  onAction: (action: DirectSaleRailAction) => void;
}) {
  const { t } = useTranslation();

  const { undo, redo } = useCanvasStateManager();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();

  const toggle = (
    action: 'upload' | 'motifs',
    icon: ReactNode,
    text: string
  ) => {
    const active = activePanel === action;
    return (
      <button
        type="button"
        aria-pressed={active}
        onClick={() => onAction(action)}
        className={cn(TILE, active ? 'bg-paper-2 text-primary' : 'text-ink-2')}
      >
        {icon}
        <span>{text}</span>
      </button>
    );
  };

  return (
    <nav
      aria-label={t('directSale.desktop.editor')}
      className="flex h-full w-[100px] flex-none flex-col items-center overflow-y-auto bg-paper py-3"
    >
      <button
        type="button"
        onClick={() => onAction('products')}
        className={cn(TILE, 'min-h-[76px] text-ink')}
      >
        <Shirt className="h-7 w-7" aria-hidden="true" />
        <span>{t('directSale.mobile.products')}</span>
      </button>

      <div className="mt-5 flex flex-col items-center gap-1">
        {toggle(
          'upload',
          <Upload className="h-5 w-5" aria-hidden="true" />,
          t('directSale.desktop.uploads')
        )}
        <button
          type="button"
          onClick={() => onAction('text')}
          className={cn(TILE, 'text-ink-2')}
        >
          <Type className="h-5 w-5" aria-hidden="true" />
          <span>{t('directSale.mobile.textShort')}</span>
        </button>
        {toggle(
          'motifs',
          <Star className="h-5 w-5" aria-hidden="true" />,
          t('directSale.design.motifs')
        )}
      </div>

      {/* mt-auto pins the history to the bottom; pt keeps a gap when the
          window is so low that the rail scrolls. */}
      <div className="mt-auto flex flex-col items-center gap-1 pt-4">
        <button
          type="button"
          aria-label={t('directSale.design.undo')}
          onClick={() => void undo()}
          disabled={!canUndo}
          className={cn(
            HISTORY,
            canUndo ? 'text-ink-2 hover:bg-paper-2' : 'text-ink-3'
          )}
        >
          <Undo2 className="h-5 w-5" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={t('directSale.design.redo')}
          onClick={() => void redo()}
          disabled={!canRedo}
          className={cn(
            HISTORY,
            canRedo ? 'text-ink-2 hover:bg-paper-2' : 'text-ink-3'
          )}
        >
          <Redo2 className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}
